import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import PersonRemoveIcon from "@mui/icons-material/PersonRemoveAlt1";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import { AutoTaskAuthContext } from "../../../../Context/AutoTaskContext";
import {
  departmentService,
  isEndpointMissing,
  MEMBERSHIP_ROLES,
  readApiError,
} from "../../services/departmentService";
import { countDescendants } from "./treeUtils";
import MemberAssignDialog from "./MemberAssignDialog";
import AttachTemplateDialog from "./AttachTemplateDialog";
import TemplateFormModal from "../TemplateFormModal";
import {
  EmptyState,
  Hint,
  initialsOf,
  PrimaryButton,
  QuietButton,
  readinessOf,
  ROLE_WORDS,
  StatPill,
  T,
  TYPE_WORDS,
} from "./ui";

// One line at the top of each tab saying what the tab is for. Cheap, and it
// removes the guesswork that made this panel opaque — "People" and "Templates"
// as bare words do not say how they relate to opening a job.
const TAB_HELP = {
  templates:
    "A job template is the description and screening questions, written once. Jobs are opened from it.",
  people:
    "Whoever is here gets prefilled onto jobs opened from this role. Someone can be in several departments at once.",
  jobs: "Live and closed jobs that were opened from this role.",
};

const NodeDetailPanel = ({
  node,
  departmentId,
  breadcrumb,
  store_id,
  user_id,
  onEditNode,
  onAddChild,
  onTreeChanged,
  onCreateJob,
}) => {
  const navigate = useNavigate();
  const contextData = useContext(AutoTaskAuthContext) || {};
  const { allUsers } = contextData;

  // Templates first: opening a job is what this screen exists for, and the
  // first version opened on People, which buried it.
  const [tab, setTab] = useState("templates");
  const [members, setMembers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [apiMissing, setApiMissing] = useState(false);
  const [showInherited, setShowInherited] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [templateFormOpen, setTemplateFormOpen] = useState(false);
  const [busyTemplateId, setBusyTemplateId] = useState(null);
  const [busyMemberId, setBusyMemberId] = useState(null);

  const nameFor = useCallback(
    (id) => (allUsers || []).find((user) => user.id === id)?.name || null,
    [allUsers],
  );

  const load = useCallback(async () => {
    if (!node?.id) return;
    setLoading(true);
    setError("");
    setApiMissing(false);
    try {
      const [memberRes, templateRes, jobRes] = await Promise.all([
        departmentService.getNodeMembers(node.id, { includeInherited: true }),
        departmentService.getNodeTemplates(node.id),
        departmentService.getNodeJobs(node.id),
      ]);
      setMembers(memberRes?.data?.data || []);
      setTemplates(templateRes?.data?.data || []);
      setJobs(jobRes?.data?.data || []);
    } catch (err) {
      if (isEndpointMissing(err)) {
        setApiMissing(true);
        setMembers([]);
        setTemplates([]);
        setJobs([]);
      } else {
        setError(readApiError(err, "Could not load this team or role."));
      }
    } finally {
      setLoading(false);
    }
  }, [node?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRoleChange = async (member, nextRole) => {
    setBusyMemberId(member.id);
    try {
      await departmentService.updateMember(member.id, {
        membership_role: nextRole,
      });
      await load();
      onTreeChanged();
    } catch (err) {
      setError(readApiError(err, "Could not change that role."));
    } finally {
      setBusyMemberId(null);
    }
  };

  const handleRemove = async (member) => {
    const who = member.name || nameFor(member.user_id) || "this person";
    if (
      !window.confirm(
        `Remove ${who} from ${node.name}?\n\nThey stay in every other department and role they belong to.`,
      )
    )
      return;
    setBusyMemberId(member.id);
    try {
      await departmentService.removeMember(member.id);
      await load();
      onTreeChanged();
    } catch (err) {
      setError(readApiError(err, "Could not remove this person."));
    } finally {
      setBusyMemberId(null);
    }
  };

  const handleDetach = async (tpl) => {
    if (
      !window.confirm(
        `Move "${tpl.template_name}" back to the template library?\n\nIt stays usable, and jobs already opened from it keep their department.`,
      )
    )
      return;
    setBusyTemplateId(tpl.id);
    try {
      await departmentService.assignTemplate(tpl.id, {
        department_id: null,
        dept_node_id: null,
      });
      await load();
      onTreeChanged();
    } catch (err) {
      setError(readApiError(err, "Could not move that template."));
    } finally {
      setBusyTemplateId(null);
    }
  };

  if (!node) {
    return (
      <EmptyState
        icon={<TouchAppOutlinedIcon />}
        title="Pick a team or role"
        body="Choose one on the left to add its job templates, its people, and to open a job from it."
      />
    );
  }

  const crumbLabel = breadcrumb.map((item) => item.name).join(" › ");
  const typeWord = TYPE_WORDS[node.node_type]?.label || "Level";
  const readiness = readinessOf(node);
  const direct = members.filter((member) => !member.is_inherited);
  const inherited = members.filter((member) => member.is_inherited);
  const visibleMembers = showInherited ? [...direct, ...inherited] : direct;
  const liveTemplates = templates.filter((tpl) => tpl.status === "ACTIVE");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box sx={{ px: { xs: 2, sm: 2.75 }, pt: 2.25, pb: 1.75 }}>
        <Typography
          sx={{
            fontFamily: T.font,
            fontSize: 12,
            color: T.faint,
            mb: 0.4,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {crumbLabel}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontFamily: T.font,
              fontWeight: 800,
              fontSize: 20,
              color: T.ink,
              letterSpacing: "-0.01em",
              lineHeight: 1.2,
            }}
          >
            {node.name}
          </Typography>
          <Chip
            label={typeWord}
            size="small"
            sx={{
              fontFamily: T.font,
              fontWeight: 700,
              fontSize: 11,
              height: 21,
              bgcolor: T.lineSoft,
              color: T.muted,
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              px: 0.9,
              py: 0.3,
              borderRadius: T.radius.pill,
              bgcolor: readiness.tone === "ok" ? T.okWash : T.warnWash,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: readiness.tone === "ok" ? T.ok : T.warn,
              }}
            />
            <Typography
              sx={{
                fontFamily: T.font,
                fontSize: 11.5,
                fontWeight: 700,
                color: readiness.tone === "ok" ? T.ok : T.warn,
              }}
            >
              {readiness.label}
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }} />

          <QuietButton
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={() => onAddChild(node)}
          >
            Add inside
          </QuietButton>
          <Button
            size="small"
            onClick={() => onEditNode(node)}
            sx={{
              fontFamily: T.font,
              textTransform: "none",
              fontWeight: 600,
              color: T.muted,
              minWidth: 0,
            }}
          >
            Rename
          </Button>
        </Box>

        {/* Deliberately not a row of every number available. The tab labels
            already carry the template and job counts, and the readiness pill
            above already says what is missing — repeating them here was most of
            what made this header feel cluttered. */}
        {(countDescendants(node) > 0 || node.headcount_planned != null) && (
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1.25 }}>
            {countDescendants(node) > 0 && (
              <StatPill
                label={`team${countDescendants(node) === 1 ? "" : "s"} & roles inside`}
                value={countDescendants(node)}
              />
            )}
            {node.headcount_planned != null && (
              <StatPill label="planned headcount" value={node.headcount_planned} />
            )}
          </Box>
        )}
      </Box>

      <Divider />

      <Tabs
        value={tab}
        onChange={(event, value) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          px: 1.5,
          minHeight: 46,
          "& .MuiTab-root": {
            fontFamily: T.font,
            textTransform: "none",
            fontWeight: 700,
            fontSize: 13.5,
            minHeight: 46,
            color: T.muted,
            "&.Mui-selected": { color: T.accent },
          },
          "& .MuiTabs-indicator": { backgroundColor: T.accent, height: 2.5 },
        }}
      >
        <Tab value="templates" label={`Job templates · ${templates.length}`} />
        <Tab value="people" label={`People · ${direct.length}`} />
        <Tab value="jobs" label={`Jobs · ${jobs.length}`} />
      </Tabs>

      <Divider />

      <Box sx={{ flex: 1, overflowY: "auto", px: { xs: 2, sm: 2.75 }, py: 2.25 }}>
        <Hint sx={{ mb: 2 }}>{TAB_HELP[tab]}</Hint>

        {apiMissing && (
          <Alert
            severity="info"
            sx={{ fontFamily: T.font, mb: 2, borderRadius: T.radius.md }}
          >
            These endpoints are not deployed on this API yet, so there is nothing
            to read. The rest of the screen still works.
          </Alert>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ fontFamily: T.font, mb: 2, borderRadius: T.radius.md }}
            action={
              <QuietButton size="small" onClick={load}>
                Try again
              </QuietButton>
            }
          >
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 5 }}>
            <CircularProgress size={22} sx={{ color: T.accent }} />
          </Box>
        ) : tab === "templates" ? (
          <>
            {/* Only when there is something to add to. With none, the empty
                state below offers the very same two buttons, and showing both
                pairs at once was the clumsiest thing on this screen. */}
            {templates.length > 0 && (
              <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                <PrimaryButton
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setTemplateFormOpen(true)}
                >
                  New job template
                </PrimaryButton>
                <QuietButton size="small" onClick={() => setAttachOpen(true)}>
                  Use one from the library
                </QuietButton>
              </Box>
            )}

            {templates.length === 0 ? (
              <EmptyState
                icon={<DescriptionOutlinedIcon />}
                title="No job template here yet"
                body="Write the description and screening questions once. After that, opening this job again takes a single click."
                action={
                  <PrimaryButton
                    startIcon={<AddIcon />}
                    onClick={() => setTemplateFormOpen(true)}
                  >
                    Write the first one
                  </PrimaryButton>
                }
                secondary={
                  <QuietButton onClick={() => setAttachOpen(true)}>
                    Use one from the library
                  </QuietButton>
                }
              />
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                {templates.map((tpl) => {
                  const busy = busyTemplateId === tpl.id;
                  const inactive = tpl.status !== "ACTIVE";
                  const questionCount =
                    tpl.questions?.length ?? tpl.question_count ?? 0;
                  return (
                    <Box
                      key={tpl.id}
                      sx={{
                        border: `1px solid ${T.line}`,
                        borderRadius: T.radius.md,
                        p: 1.75,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.25,
                        bgcolor: T.surface,
                        transition: "border-color .16s",
                        "&:hover": { borderColor: T.accentLine },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: T.radius.sm,
                            bgcolor: T.canvas,
                            color: T.muted,
                            display: "grid",
                            placeItems: "center",
                            flex: "none",
                          }}
                        >
                          <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontFamily: T.font,
                              fontSize: 14.5,
                              fontWeight: 700,
                              color: T.ink,
                            }}
                          >
                            {tpl.template_name}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: T.font,
                              fontSize: 12.5,
                              color: T.muted,
                            }}
                          >
                            {tpl.title} · {questionCount} screening question
                            {questionCount === 1 ? "" : "s"}
                          </Typography>
                        </Box>
                        {inactive && (
                          <Chip
                            label="Inactive"
                            size="small"
                            sx={{
                              fontFamily: T.font,
                              fontWeight: 700,
                              fontSize: 10.5,
                              height: 20,
                              bgcolor: T.lineSoft,
                              color: T.muted,
                            }}
                          />
                        )}
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        {/* The payoff of the whole structure, so it is the
                            loudest thing on the row. */}
                        <PrimaryButton
                          size="small"
                          disabled={busy || inactive}
                          onClick={() => onCreateJob(tpl)}
                          startIcon={
                            <RocketLaunchOutlinedIcon sx={{ fontSize: 16 }} />
                          }
                        >
                          Open a job from this
                        </PrimaryButton>

                        {inactive && (
                          <Hint sx={{ fontSize: 12 }}>
                            Set the template active to open jobs from it.
                          </Hint>
                        )}

                        <Box sx={{ flex: 1 }} />

                        <Tooltip title="Move back to the library">
                          <IconButton
                            size="small"
                            disabled={busy}
                            aria-label="Move this template back to the library"
                            onClick={() => handleDetach(tpl)}
                            sx={{ color: T.faint, "&:hover": { color: T.warn } }}
                          >
                            {busy ? (
                              <CircularProgress size={15} />
                            ) : (
                              <LinkOffIcon sx={{ fontSize: 17 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        ) : tab === "people" ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
                flexWrap: "wrap",
              }}
            >
              {direct.length > 0 && (
                <PrimaryButton
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setAssignOpen(true)}
                >
                  Add a person
                </PrimaryButton>
              )}
              <Box sx={{ flex: 1 }} />
              {inherited.length > 0 && (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showInherited}
                      onChange={(event) => setShowInherited(event.target.checked)}
                      sx={{
                        "& .Mui-checked": { color: T.accent },
                        "& .Mui-checked + .MuiSwitch-track": {
                          backgroundColor: T.accent,
                        },
                      }}
                    />
                  }
                  label={
                    <Hint sx={{ fontSize: 12.5 }}>
                      Also show {inherited.length} from above
                    </Hint>
                  }
                />
              )}
            </Box>

            {visibleMembers.length === 0 ? (
              <EmptyState
                icon={<GroupsOutlinedIcon />}
                title="Nobody on this role yet"
                body="Add the hiring manager, recruiter and interviewers. They get prefilled onto every job opened from here."
                action={
                  <PrimaryButton
                    startIcon={<AddIcon />}
                    onClick={() => setAssignOpen(true)}
                  >
                    Add the first person
                  </PrimaryButton>
                }
              />
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {visibleMembers.map((member) => {
                  const name =
                    member.name || nameFor(member.user_id) || "Unknown user";
                  const busy = busyMemberId === member.id;
                  return (
                    <Box
                      key={member.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        border: `1px solid ${
                          member.is_inherited ? T.lineSoft : T.line
                        }`,
                        borderRadius: T.radius.md,
                        px: 1.5,
                        py: 1.15,
                        bgcolor: member.is_inherited ? T.canvas : T.surface,
                        flexWrap: "wrap",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: 12,
                          fontFamily: T.font,
                          fontWeight: 700,
                          bgcolor: T.accentWash,
                          color: T.accentDeep,
                        }}
                      >
                        {initialsOf(name)}
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 120 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                        >
                          <Typography
                            sx={{
                              fontFamily: T.font,
                              fontSize: 14,
                              fontWeight: 700,
                              color: T.ink,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {name}
                          </Typography>
                          {member.is_primary && (
                            <Tooltip title="This is their main team — one per person">
                              <Chip
                                label="Main"
                                size="small"
                                sx={{
                                  fontFamily: T.font,
                                  fontWeight: 700,
                                  fontSize: 10,
                                  height: 18,
                                  bgcolor: T.accentWash,
                                  color: T.accentDeep,
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography
                          sx={{ fontFamily: T.font, fontSize: 12, color: T.faint }}
                        >
                          {member.is_inherited
                            ? `Comes from ${
                                member.source_node_name || "a level above"
                              }`
                            : member.other_placement_count
                            ? `Also in ${member.other_placement_count} other place${
                                member.other_placement_count === 1 ? "" : "s"
                              }`
                            : "Only here"}
                        </Typography>
                      </Box>

                      {member.is_inherited ? (
                        <Tooltip title="Change this where it was set, one level up">
                          <Chip
                            label={
                              ROLE_WORDS[member.membership_role]?.label ||
                              member.membership_role
                            }
                            size="small"
                            sx={{
                              fontFamily: T.font,
                              fontWeight: 600,
                              fontSize: 11,
                              height: 22,
                              bgcolor: T.lineSoft,
                              color: T.muted,
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <>
                          <Select
                            size="small"
                            value={member.membership_role || "MEMBER"}
                            disabled={busy}
                            onChange={(event) =>
                              handleRoleChange(member, event.target.value)
                            }
                            sx={{
                              fontFamily: T.font,
                              fontSize: 13,
                              minWidth: 156,
                              borderRadius: T.radius.sm,
                              "& .MuiSelect-select": { py: 0.65 },
                            }}
                          >
                            {MEMBERSHIP_ROLES.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                <Box>
                                  <Typography
                                    sx={{ fontFamily: T.font, fontSize: 13.5 }}
                                  >
                                    {option.label}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontFamily: T.font,
                                      fontSize: 11,
                                      color: T.faint,
                                    }}
                                  >
                                    {ROLE_WORDS[option.value]?.hint}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                          <Tooltip title={`Remove from ${node.name}`}>
                            <IconButton
                              size="small"
                              disabled={busy}
                              aria-label={`Remove ${name} from ${node.name}`}
                              onClick={() => handleRemove(member)}
                              sx={{
                                color: T.faint,
                                "&:hover": { color: T.danger },
                              }}
                            >
                              {busy ? (
                                <CircularProgress size={15} />
                              ) : (
                                <PersonRemoveIcon sx={{ fontSize: 17 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        ) : jobs.length === 0 ? (
          <EmptyState
            icon={<WorkOutlineIcon />}
            title="No jobs opened from here yet"
            body={
              liveTemplates.length
                ? "You have a job template ready — open a job from the Job templates tab."
                : "Add a job template first, then you can open a job in one click."
            }
            action={
              <PrimaryButton onClick={() => setTab("templates")}>
                Go to job templates
              </PrimaryButton>
            }
          />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {jobs.map((job) => (
              <Box
                key={job.id}
                onClick={() => navigate(`/ats/job/${job.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter") navigate(`/ats/job/${job.id}`);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  border: `1px solid ${T.line}`,
                  borderRadius: T.radius.md,
                  px: 1.75,
                  py: 1.25,
                  cursor: "pointer",
                  transition: "border-color .16s, background .16s",
                  "&:hover": { borderColor: T.accentLine, bgcolor: T.accentWash },
                  "&:focus-visible": { outline: `2px solid ${T.accent}` },
                }}
              >
                <WorkOutlineIcon sx={{ fontSize: 19, color: T.accent }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: T.font,
                      fontSize: 14,
                      fontWeight: 700,
                      color: T.ink,
                    }}
                  >
                    {job.title}
                  </Typography>
                  <Typography
                    sx={{ fontFamily: T.font, fontSize: 12, color: T.faint }}
                  >
                    {job.job_code ? `${job.job_code} · ` : ""}
                    {job.vacancies ?? 1} opening
                    {(job.vacancies ?? 1) === 1 ? "" : "s"}
                    {job.location ? ` · ${job.location}` : ""}
                  </Typography>
                </Box>
                <Chip
                  label={job.status === "ACTIVE" ? "Open" : "Closed"}
                  size="small"
                  sx={{
                    fontFamily: T.font,
                    fontWeight: 700,
                    fontSize: 11,
                    height: 22,
                    bgcolor: job.status === "ACTIVE" ? T.okWash : T.lineSoft,
                    color: job.status === "ACTIVE" ? T.ok : T.muted,
                  }}
                />
                <OpenInNewIcon sx={{ fontSize: 15, color: T.line }} />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <AttachTemplateDialog
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        store_id={store_id}
        departmentId={departmentId}
        node={node}
        breadcrumb={breadcrumb}
        onAttached={() => {
          load();
          onTreeChanged();
        }}
      />

      <TemplateFormModal
        open={templateFormOpen}
        onClose={() => setTemplateFormOpen(false)}
        template={null}
        store_id={store_id}
        user_id={user_id}
        departmentId={departmentId}
        nodeId={node?.id}
        contextLabel={crumbLabel}
        onSaved={() => {
          load();
          onTreeChanged();
        }}
      />

      <MemberAssignDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        departmentId={departmentId}
        node={node}
        breadcrumb={breadcrumb}
        existingMembers={direct}
        store_id={store_id}
        user_id={user_id}
        onSaved={() => {
          load();
          onTreeChanged();
        }}
      />
    </Box>
  );
};

export default NodeDetailPanel;
